import { config } from "@/config/env";
import * as Sentry from "@sentry/react";

/**
 * Robust logging utility matching Enterprise patterns.
 *
 * All application logs should use this utility instead of console.log directly.
 * In development, it logs visually to the console.
 * In production, it silences the visual console logs but uses Sentry:
 *  - .error() and .logFatal(): Send immediate issue/alert Events to Sentry.
 *  - .warn(), .info(), .log(), and .debug(): Send directly to Sentry's Logs product.
 * 
 * Usage:
 * import { logger } from '@/utils/logger';
 * logger.info('User initiated action'); // Sent to Sentry Logs
 * logger.error('Action failed', error, { userId }); // Alert + Context
 */

export const logger = {
  log: (...args: any[]) => {
    if (config.isDevelopment) {
      console.log(...args);
    } else {
      Sentry.logger.info(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
    }
  },
  warn: (message: string, context?: any, ..._rest: any[]) => {
    if (config.isDevelopment) {
      console.warn(message, context ?? "", ..._rest);
    } else {
      const ctx = context && typeof context === "object" && !Array.isArray(context) ? context : context !== undefined ? { value: context } : {};
      Sentry.logger.warn(message, ctx);
    }
  },
  error: (message: string, error?: any, context?: any, ..._rest: any[]) => {
    if (config.isDevelopment) {
      console.error(message, error || "", context || "");
    } else {
      Sentry.withScope((scope) => {
        if (context) {
          scope.setExtras(context);
        }
        
        if (error instanceof Error) {
          scope.setExtra("custom_message", message);
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`${message} ${error ? JSON.stringify(error) : ''}`, "error");
        }
      });
    }
  },
  info: (message: string, context?: Record<string, any>) => {
    if (config.isDevelopment) {
      console.info(message, context || "");
    } else {
      Sentry.logger.info(message, context || {});
    }
  },
  debug: (...args: any[]) => {
    if (config.isDevelopment) {
      console.debug(...args);
    } else {
      Sentry.logger.debug(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
    }
  },
};
